import { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { BookingPeriodDateFields } from "@/components/ui/BookingPeriodDateFields";
import {
  amendInquiry,
  previewInquiryAmendment,
  type InquiryAmendInput,
  type InquiryAmendPreview,
} from "@/lib/bookingInquiryActions";
import { formatIsoDateShort, todayLocal } from "@/lib/bookingDates";
import { formatStayAmount } from "@/lib/money";
import { usePitchCategories } from "@/lib/usePitchCategories";
import type { Booking, PitchCategory } from "@/types/camping";

type AmendMode = "category" | "duration";

function nightsLabel(nights: number): string {
  return nights === 1 ? "1 Nacht" : `${nights} Nächte`;
}

function requestedCategoryId(booking: Booking): string | null {
  const offer = booking.inquiry_offer;
  if (offer?.requested_recorded && offer.requested_category_id) return offer.requested_category_id;
  return booking.pitch_category_id;
}

function requestedCategoryName(booking: Booking, categoryName?: string | null): string {
  const offer = booking.inquiry_offer;
  if (offer?.requested_recorded && offer.requested_category_name?.trim()) {
    return offer.requested_category_name.trim();
  }
  return categoryName?.trim() || "Ohne Kategorie";
}

export interface InquiryAmendPanelProps {
  booking: Booking;
  categoryName?: string | null;
  disabled?: boolean;
  onApplied: (booking: Booking) => void;
  onBusyChange?: (busy: boolean) => void;
}

export function InquiryAmendPanel({
  booking,
  categoryName,
  disabled = false,
  onApplied,
  onBusyChange,
}: InquiryAmendPanelProps) {
  const flexible = booking.pitch_assignment_mode !== "fixed";
  const { categories } = usePitchCategories(true);
  const [mode, setMode] = useState<AmendMode>(flexible ? "category" : "duration");
  const [categoryId, setCategoryId] = useState(booking.pitch_category_id ?? "");
  const [arrival, setArrival] = useState(booking.arrival_date);
  const [departure, setDeparture] = useState(booking.departure_date);
  const [preview, setPreview] = useState<InquiryAmendPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const requestSeq = useRef(0);
  const rootRef = useRef<HTMLElement>(null);

  useEffect(() => {
    rootRef.current?.scrollIntoView({ block: "nearest" });
  }, []);

  useEffect(() => {
    setMode(flexible ? "category" : "duration");
    setCategoryId(booking.pitch_category_id ?? "");
    setArrival(booking.arrival_date);
    setDeparture(booking.departure_date);
    setPreview(null);
    setPreviewError(null);
    setSaveError(null);
  }, [booking.id, booking.version, flexible, booking.pitch_category_id, booking.arrival_date, booking.departure_date]);

  const datesInvalid = Boolean(arrival && departure && departure <= arrival);
  const dirty =
    mode === "category"
      ? Boolean(categoryId) && categoryId !== (booking.pitch_category_id ?? "")
      : Boolean(
          arrival &&
            departure &&
            !datesInvalid &&
            (arrival !== booking.arrival_date || departure !== booking.departure_date),
        );

  const version = booking.version ?? 1;
  const payload: InquiryAmendInput | null = dirty
    ? mode === "category"
      ? { version, pitch_category_id: categoryId }
      : { version, arrival_date: arrival, departure_date: departure }
    : null;
  const previewKey = payload
    ? mode === "category"
      ? `category:${version}:${categoryId}`
      : `duration:${version}:${arrival}:${departure}`
    : "";
  const payloadRef = useRef(payload);
  payloadRef.current = payload;

  useEffect(() => {
    const input = payloadRef.current;
    if (!previewKey || !input) {
      requestSeq.current += 1;
      setPreview(null);
      setPreviewError(null);
      setPreviewLoading(false);
      return;
    }
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setPreviewLoading(true);
    setPreviewError(null);
    const timer = window.setTimeout(() => {
      void previewInquiryAmendment(booking.id, input)
        .then((result) => {
          if (requestSeq.current !== seq) return;
          setPreview(result);
        })
        .catch((error: unknown) => {
          if (requestSeq.current !== seq) return;
          setPreview(null);
          setPreviewError(error instanceof Error ? error.message : "Vorschau nicht möglich.");
        })
        .finally(() => {
          if (requestSeq.current === seq) setPreviewLoading(false);
        });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [booking.id, previewKey]);

  useEffect(() => {
    onBusyChange?.(saving);
  }, [onBusyChange, saving]);

  const originalArrival = booking.inquiry_offer?.requested_arrival_date ?? booking.arrival_date;
  const originalDeparture = booking.inquiry_offer?.requested_departure_date ?? booking.departure_date;
  const originalCategory = requestedCategoryName(booking, categoryName);
  const categoryResets =
    mode === "duration" && requestedCategoryId(booking) !== booking.pitch_category_id;

  const categoryOptions: PitchCategory[] = [...categories];
  if (
    booking.pitch_category_id &&
    !categoryOptions.some((category) => category.id === booking.pitch_category_id)
  ) {
    categoryOptions.unshift({
      id: booking.pitch_category_id,
      name: categoryName?.trim() || "Aktuelle Kategorie",
      color: "",
      sort_order: -1,
      is_active: true,
    });
  }

  async function submit() {
    if (!payload || saving || disabled) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await amendInquiry(booking.id, payload);
      onApplied(updated);
    } catch (error: unknown) {
      setSaveError(error instanceof Error ? error.message : "Die Änderung konnte nicht gespeichert werden.");
    } finally {
      setSaving(false);
    }
  }

  const canSubmit =
    Boolean(payload && preview) && !previewLoading && !previewError && !datesInvalid && !saving && !disabled;
  const confirmLabel = preview && preview.changes.length > 0 ? "Geändert bestätigen" : "Anpassung bestätigen";

  return (
    <section ref={rootRef} className="rounded-xl border border-separator bg-fill-tertiary/30 p-4">
      <h3 className="text-[11px] font-semibold uppercase tracking-wider text-label-tertiary">
        Anfrage anpassen
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-label-secondary">
        Es gilt eine Änderung: entweder eine andere Platzkategorie oder ein anderer Zeitraum. Der
        Hinweis darauf steht in der Bestätigung an den Gast.
      </p>
      <p className="mt-2 text-sm text-label">
        Ursprüngliche Anfrage: {originalCategory} · {formatIsoDateShort(originalArrival)}–
        {formatIsoDateShort(originalDeparture)}
      </p>

      {flexible ? (
        <div className="eh-segmented mt-4" role="tablist" aria-label="Was geändert wird">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "category"}
            className={clsx("eh-segmented-item", mode === "category" && "is-active")}
            disabled={saving || disabled}
            onClick={() => setMode("category")}
          >
            Platzkategorie
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "duration"}
            className={clsx("eh-segmented-item", mode === "duration" && "is-active")}
            disabled={saving || disabled}
            onClick={() => setMode("duration")}
          >
            Zeitraum
          </button>
        </div>
      ) : (
        <p className="mt-3 text-sm text-label-secondary">
          Der Platz ist fest zugewiesen. Sie können den Zeitraum ändern.
        </p>
      )}

      {mode === "category" ? (
        <label className="mt-4 block text-sm">
          <span className="font-medium text-label">Neue Platzkategorie</span>
          <select
            className="eh-input mt-1.5 w-full"
            value={categoryId}
            disabled={saving || disabled}
            onChange={(event) => setCategoryId(event.target.value)}
          >
            <option value="">Kategorie wählen</option>
            {categoryOptions.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
          <span className="mt-1.5 block text-label-secondary">
            Der Zeitraum bleibt bei der ursprünglichen Anfrage.
          </span>
        </label>
      ) : (
        <div className="mt-4">
          <BookingPeriodDateFields
            arrivalId={`inquiry-amend-arrival-${booking.id}`}
            departureId={`inquiry-amend-departure-${booking.id}`}
            arrivalLabel="Anreise"
            departureLabel="Abreise"
            arrival={arrival}
            departure={departure}
            arrivalMin={todayLocal()}
            departureMin={arrival || todayLocal()}
            onArrivalChange={setArrival}
            onDepartureChange={setDeparture}
          />
          <p className="mt-1.5 text-sm text-label-secondary">
            Die Platzkategorie bleibt die ursprünglich angefragte.
            {categoryResets ? " Die aktuell angebotene Kategorie wird zurückgesetzt." : ""}
          </p>
          {datesInvalid ? (
            <p className="mt-1.5 text-sm text-status-red">Abreise muss nach der Anreise liegen.</p>
          ) : null}
        </div>
      )}

      {previewLoading ? <p className="mt-3 text-sm text-label-secondary">Preis wird berechnet…</p> : null}
      {previewError ? (
        <Alert variant="error" className="mt-3">
          {previewError}
        </Alert>
      ) : null}
      {saveError ? (
        <Alert variant="error" className="mt-3">
          {saveError}
        </Alert>
      ) : null}

      {preview && !previewError ? (
        <div className="mt-3 rounded-xl border border-status-orange/30 bg-status-orange/10 px-4 py-3 text-sm text-label">
          <p className="font-medium">
            {preview.pitch_category_name?.trim() || "Ohne Kategorie"} ·{" "}
            {formatIsoDateShort(preview.arrival_date)}–{formatIsoDateShort(preview.departure_date)} ·{" "}
            {nightsLabel(preview.nights)}
          </p>
          <p className="mt-1 text-base font-semibold tabular-nums">
            {formatStayAmount(preview.total_amount, preview.currency, preview.price_pending)}
          </p>
          {preview.changes.length > 0 ? (
            <>
              <p className="mt-3 font-medium">Hinweis in der Bestätigung</p>
              <ul className="mt-1 list-disc space-y-1 pl-5">
                {preview.changes.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </>
          ) : (
            <p className="mt-3 text-label-secondary">
              Damit entspricht die Buchung wieder der ursprünglichen Anfrage. Die Bestätigung enthält
              keinen Änderungshinweis.
            </p>
          )}
        </div>
      ) : null}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center">
        <Button type="button" loading={saving} disabled={!canSubmit} onClick={() => void submit()}>
          {confirmLabel}
        </Button>
        <p className="text-xs leading-relaxed text-label-tertiary">
          Speichert die Änderung und öffnet die E-Mail. Mit dem Versand wird die Anfrage bestätigt.
        </p>
      </div>
    </section>
  );
}
